<template>
  <section class="sheet-backup-download">
    <h4 class="sheet-backup-download__title">Descargar respaldos de Google Sheets</h4>
    <p class="sheet-backup-download__hint">
      Selecciona un rango de fechas para descargar un Excel con las pestañas
      Envíos y Retiros, incluyendo todos los días del rango. Los días con
      formatos anteriores se normalizan al formato actual.
    </p>

    <form class="sheet-backup-download__form" @submit.stop.prevent="onSubmit">
      <div class="sheet-backup-download__dates">
        <AppField label="Desde" :state="dateState">
          <AppInput
            id="sheet-backup-from"
            v-model="from"
            type="date"
            aria-label="Fecha desde"
            :max="to || null"
            :aria-invalid="dateError ? 'true' : 'false'"
          />
        </AppField>

        <AppField label="Hasta" :state="dateState">
          <AppInput
            id="sheet-backup-to"
            v-model="to"
            type="date"
            aria-label="Fecha hasta"
            :min="from || null"
            :aria-invalid="dateError ? 'true' : 'false'"
          />
        </AppField>
      </div>

      <p v-if="dateError" class="sheet-backup-download__error" role="alert">
        {{ dateError }}
      </p>

      <div class="sheet-backup-download__actions">
        <AppButton type="submit" variant="primary" :disabled="downloading">
          {{ downloading ? 'Generando Excel...' : 'Descargar Excel' }}
        </AppButton>
      </div>
    </form>
  </section>
</template>

<script>
import moment from 'moment-timezone'
import { mapActions } from 'vuex'
import AppField from '../UI/AppField'
import AppInput from '../UI/AppInput'
import AppButton from '../UI/AppButton'

const getDefaultRange = () => {
  const yesterday = moment().tz('America/Santiago').subtract(1, 'day')
  return {
    from: yesterday.clone().subtract(6, 'days').format('YYYY-MM-DD'),
    to: yesterday.format('YYYY-MM-DD')
  }
}

export default {
  name: 'SheetBackupDownload',
  components: {
    AppField,
    AppInput,
    AppButton
  },
  data () {
    const range = getDefaultRange()
    return {
      from: range.from,
      to: range.to,
      downloading: false
    }
  },
  computed: {
    dateError () {
      if (!this.from || !this.to) {
        return 'Debes seleccionar la fecha desde y hasta.'
      }
      if (this.from > this.to) {
        return 'La fecha desde no puede ser posterior a la fecha hasta.'
      }
      return ''
    },
    dateState () {
      return this.dateError ? false : null
    }
  },
  methods: {
    ...mapActions('settlement', ['downloadSheetBackupExcel']),
    async onSubmit () {
      if (this.dateError) {
        this.$toast.warning(this.dateError)
        return
      }

      this.downloading = true
      try {
        await this.downloadSheetBackupExcel({
          params: { from: this.from, to: this.to }
        })
        this.$toast.success('Respaldo descargado correctamente')
      } catch (error) {
        this.$toast.danger(await this.getErrorMessage(error))
      } finally {
        this.downloading = false
      }
    },
    async getErrorMessage (error) {
      const responseData = error && error.response && error.response.data

      if (typeof Blob !== 'undefined' && responseData instanceof Blob) {
        try {
          const message = this.extractMessage(await responseData.text())
          if (message) return message
        } catch {
          // Si el blob no se puede leer, se usa el mensaje genérico.
        }
      }

      if (typeof responseData === 'string') {
        const message = this.extractMessage(responseData)
        if (message) return message
      }

      if (responseData && responseData.message) {
        return responseData.message
      }

      return 'No se pudo descargar el respaldo'
    },
    extractMessage (raw) {
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        return parsed && parsed.message ? parsed.message : ''
      } catch {
        return ''
      }
    }
  }
}
</script>

<style scoped>
.sheet-backup-download {
  display: grid;
  gap: 1rem;
}

.sheet-backup-download__title {
  margin: 0;
}

.sheet-backup-download__hint {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.sheet-backup-download__form {
  display: grid;
  gap: 0.5rem;
}

.sheet-backup-download__dates {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.sheet-backup-download__actions {
  display: flex;
  justify-content: flex-end;
}

.sheet-backup-download__error {
  margin: 0;
  color: var(--app-danger, #dc3545);
  font-size: 0.875rem;
}

@media (max-width: 640px) {
  .sheet-backup-download__dates {
    grid-template-columns: 1fr;
  }
}
</style>
